import React, { useContext, useEffect, useState } from 'react';
import { API_BASE_URL, SCOPES } from '@techchange/components';
import axios from 'axios';
import PropTypes from 'prop-types';
import qs from 'qs';
import { Redirect, useLocation } from 'react-router-dom';

import types from '../utils/types';

const axiosClient = axios.create({
	baseURL: API_BASE_URL.FORM_ENTRIES,
	withCredentials: true,
});

const PostRegistrationContext = React.createContext();

const URL_TERMS_OF_SERVICE_PAGE = '/register/terms-of-service';
const URL_POST_REGISTRATION_PAGE = '/settings/post-register';

const POST_REGISTRATION_ENTRY_STATUS = {
	LOADING: 'loading',
	COMPLETE: 'complete',
	MISSING: 'missing',
	ERROR: 'error',
};

const propTypes = {

	/**
	 * The current user, if any.
	 */
	currentUser: types.currentUser,

	/**
	 * The organization.
	 */
	myOrganization: types.myOrganization.isRequired,

	/**
	 * The component children.
	 */
	children: PropTypes.node.isRequired,

};

/**
 * The post registration form wrapping provider.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object} The provider component.
 */
const PostRegistrationWrapper = (props) => {

	const { currentUser, myOrganization, children } = props;

	const isRequired = Boolean(myOrganization?.data?.post_registration_form);

	const [status, setStatus] = useState(POST_REGISTRATION_ENTRY_STATUS.LOADING);

	const { pathname, search } = useLocation();

	const query = qs.parse(search, { ignoreQueryPrefix: true });

	useEffect(() => {

		const formID = myOrganization?.data?.post_registration_form;

		if (formID && currentUser) {

			axiosClient.get('/', {
				params: {
					form_id: formID,
					user_id: currentUser.id,
					scope: SCOPES.ORGANIZATION,
					scope_id: myOrganization.id,
				}
			}).then(({ data: formEntries }) => {

				if (formEntries.length === 0) {

					setStatus(POST_REGISTRATION_ENTRY_STATUS.MISSING);

				} else {

					setStatus(POST_REGISTRATION_ENTRY_STATUS.COMPLETE);

				}

			}).catch((error) => {

				console.error(error);
				setStatus(POST_REGISTRATION_ENTRY_STATUS.ERROR);

			});

		}

	}, [currentUser, myOrganization]);

	// Check if the user is missing an entry for the required post registration form
	if (isRequired &&
		currentUser &&
		status === POST_REGISTRATION_ENTRY_STATUS.MISSING &&
		!pathname.startsWith(URL_TERMS_OF_SERVICE_PAGE) &&
		!pathname.startsWith(URL_POST_REGISTRATION_PAGE)) {

		// After they complete the form, redirect the user to their original page

		const redirect = query.redirect
			? decodeURIComponent(query.redirect)
			: `${pathname}${search}`;

		let redirectURL = URL_POST_REGISTRATION_PAGE;

		if (redirect !== URL_POST_REGISTRATION_PAGE) {
			redirectURL += `?redirect=${encodeURIComponent(redirect)}`;
		}

		return <Redirect to={redirectURL} />;

	}

	return (
		<PostRegistrationContext.Provider
			value={{
				isPostRegistrationRequired: isRequired,
				postRegistrationStatus: status,
				setPostRegistrationStatus: setStatus,
			}}>
			{children}
		</PostRegistrationContext.Provider>
	);

};

PostRegistrationWrapper.propTypes = propTypes;

/**
 * The post registration form wrapping context hook.
 *
 * @returns {Object} context The shared context state.
 *
 * @returns {Boolean} context.isPostRegistrationRequired
 * True, if the post registration form is required.
 *
 * @returns {Object} context.postRegistrationStatus
 * The status of the post registration form state for the current user.
 *
 * @returns {Object} context.setPostRegistrationStatus
 * Callback function to update the status of the post registration form state.
 */
function usePostRegistrationContext() {

	const context = useContext(PostRegistrationContext);

	if (!context) {

		throw new Error(
			'usePostRegistrationContext must be used within a PostRegistrationWrapper'
		);

	}

	return context;

}

export {
	PostRegistrationWrapper,
	usePostRegistrationContext,
};
